import { useState, useEffect } from 'react';
import { NewsArticle, PaginatedResponse } from '../types';
import { newsApi } from '../services/api';

interface UseNewsProps {
  categoryId?: string;
  initialPage?: number;
  pageSize?: number;
}

export const useNews = (props?: UseNewsProps) => {
  const { categoryId, initialPage = 0, pageSize = 30 } = props || {};
  
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [pagination, setPagination] = useState<Omit<PaginatedResponse<NewsArticle>, 'content'>>({
    totalPages: 0,
    totalElements: 0,
    size: pageSize,
    number: initialPage,
    first: true,
    last: true,
    empty: true
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [featuredNews, setFeaturedNews] = useState<NewsArticle[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState<boolean>(true);
  const [latestNews, setLatestNews] = useState<NewsArticle[]>([]);
  const [loadingLatest, setLoadingLatest] = useState<boolean>(true);

  const fetchNews = async (page: number = pagination.number) => {
    try {
      setLoading(true);
      let response: PaginatedResponse<NewsArticle>;
      
      if (categoryId) {
        response = await newsApi.getByCategory(categoryId, page, pageSize);
      } else {
        response = await newsApi.getAll(page, pageSize);
      }
      
      setNews(response.content);
      setPagination({
        totalPages: response.totalPages,
        totalElements: response.totalElements,
        size: response.size,
        number: response.number,
        first: response.first,
        last: response.last,
        empty: response.empty
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch news articles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedNews = async () => {
    try {
      setLoadingFeatured(true);
      const featured = await newsApi.getFeatured();
      setFeaturedNews(featured);
    } catch (err) {
      console.error('Failed to fetch featured news:', err);
    } finally {
      setLoadingFeatured(false);
    }
  };

  const fetchLatestNews = async (size: number = 10) => {
    try {
      setLoadingLatest(true);
      const latest = await newsApi.getLatest(size);
      setLatestNews(latest);
    } catch (err) {
      console.error('Failed to fetch latest news:', err);
    } finally {
      setLoadingLatest(false);
    }
  };

  useEffect(() => {
    // CategoryPage'den geliyorsa ve categoryId henüz undefined ise fetch yapma
    if (props && 'categoryId' in props && categoryId === undefined) {
      setLoading(false);
      return;
    }
    
    fetchNews(initialPage);
    fetchFeaturedNews();
    fetchLatestNews();
  }, [categoryId, initialPage, pageSize]);

  const changePage = (newPage: number) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      fetchNews(newPage);
    }
  };

  const refreshNews = () => {
    fetchNews(pagination.number);
    fetchFeaturedNews();
  };

  const addArticle = async (article: FormData) => {
    try {
      const newArticle = await newsApi.create(article);
      refreshNews();
      return newArticle;
    } catch (err) {
      console.error('Failed to add article:', err);
      throw err;
    }
  };

  const updateArticle = async (id: string, article: FormData) => {
    try {
      const updatedArticle = await newsApi.update(id, article);
      setNews(news.map(n => n.id === id ? updatedArticle : n));
      if (featuredNews.some(n => n.id === id)) {
        setFeaturedNews(featuredNews.map(n => n.id === id ? updatedArticle : n));
      }
      return updatedArticle;
    } catch (err) {
      console.error('Failed to update article:', err);
      throw err;
    }
  };

  const deleteArticle = async (id: string) => {
    try {
      await newsApi.delete(id);
      setNews(news.filter(n => n.id !== id));
      setFeaturedNews(featuredNews.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete article:', err);
      throw err;
    }
  };

  return {
    news,
    pagination,
    loading,
    error,
    featuredNews,
    loadingFeatured,
    latestNews,
    loadingLatest,
    changePage,
    refreshNews,
    addArticle,
    updateArticle,
    deleteArticle,
    fetchLatestNews
  };
};

export default useNews; 